import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { loginRequest, logoutRequest, refreshRequest } from "../api/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [accessToken, setAccessToken] = useState(null);
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  // Prevent React.StrictMode dev double-run
  const bootStartedRef = useRef(false);

  // Used to ignore stale boot refresh results after manual auth actions
  const authActionVersionRef = useRef(0);

  useEffect(() => {
    if (bootStartedRef.current) {
      return;
    }

    bootStartedRef.current = true;
    let isMounted = true;
    const bootVersion = authActionVersionRef.current;

    async function bootAuth() {
      try {
        const response = await refreshRequest();

        // Ignore if unmounted or if a login/logout happened after this started
        if (!isMounted || authActionVersionRef.current !== bootVersion) {
          return;
        }

        setAccessToken(response.data.access_token);
        setUser(response.data.user);
      } catch (error) {
        // Ignore stale result if login/logout already happened
        if (!isMounted || authActionVersionRef.current !== bootVersion) {
          return;
        }

        setAccessToken(null);
        setUser(null);
      } finally {
        if (!isMounted || authActionVersionRef.current !== bootVersion) {
          return;
        }

        setAuthLoading(false);
      }
    }

    bootAuth();

    return () => {
      isMounted = false;
    };
  }, []);

  async function login(email, password) {
    // Invalidate any in-flight bootAuth result
    authActionVersionRef.current += 1;

    const response = await loginRequest(email, password);

    setAccessToken(response.data.access_token);
    setUser(response.data.user);
    setAuthLoading(false);

    return response;
  }

  async function logout() {
    // Invalidate any in-flight bootAuth result
    authActionVersionRef.current += 1;

    try {
      await logoutRequest();
    } catch (error) {
      // even if API fails, still clear client auth state
    } finally {
      setAccessToken(null);
      setUser(null);
      setAuthLoading(false);
    }
  }

  async function refreshAccessToken() {
    try {
      const response = await refreshRequest();
      setAccessToken(response.data.access_token);
      setUser(response.data.user);
      return response.data.access_token;
    } catch (error) {
      setAccessToken(null);
      setUser(null);
      return null;
    }
  }

  const value = useMemo(
    () => ({
      accessToken,
      user,
      authLoading,
      isAuthenticated: !!accessToken,
      login,
      logout,
      refreshAccessToken,
      setAccessToken,
      setUser,
    }),
    [accessToken, user, authLoading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used inside AuthProvider");
  }

  return context;
}
